import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, addDoc, onSnapshot } from "firebase/firestore";
import Spinner from './Spinner.js';
import { getNextMocNumber } from './firebaseUtils.js';
import { updateDispatchAudit } from './utils/dispatchAudit.js';
import { ArrowLeft, Edit2, CheckCircle, Printer, Send, Package, Clock, AlertCircle } from 'lucide-react';
import { isAdminUser, isOpsManager, isOpsUser } from './accessControl.js';
import HorizontalTimeline from './components/HorizontalTimeline.js';
import MOCPreviewContent from './MOCPreviewContent.js';
import OrderPreview from './OrderPreview.js';
import StatusBadge, { getDispatchStatusBadgeMeta } from './components/StatusBadge.js';

const MOCPreview = ({ db, user }) => {
  const { mocId } = useParams();
  const navigate = useNavigate();
  const [moc, setMoc] = useState(null);
  const [order, setOrder] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [sourceWarehouse, setSourceWarehouse] = useState(null);
  const [destinationWarehouse, setDestinationWarehouse] = useState(null);
  const [linkedOrder, setLinkedOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [firms, setFirms] = useState([]);
  const [activeTab, setActiveTab] = useState('moc');
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [opsReviewRemarks, setOpsReviewRemarks] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const printRef = useRef();

  useEffect(() => {
    const fetchMoc = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "dispatches", mocId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const mocData = { id: docSnap.id, ...docSnap.data() };
          setMoc(mocData);

          if (mocData.orderId) {
            const [orderSnap, firmSnap] = await Promise.all([
              getDoc(doc(db, "orders", mocData.orderId)),
              getDocs(collection(db, "firms"))
            ]);

            if (orderSnap.exists()) {
              const orderData = { id: orderSnap.id, ...orderSnap.data() };
              setOrder(orderData);
              setLinkedOrder(orderData);

              if (orderData.customerID) {
                const customerSnap = await getDoc(doc(db, "customers", orderData.customerID));
                if (customerSnap.exists()) {
                  setCustomer({ id: customerSnap.id, ...customerSnap.data() });
                } else {
                  setCustomer(null);
                }
              } else {
                setCustomer(null);
              }

              setFirms(firmSnap.docs.map(firmDoc => ({ id: firmDoc.id, ...firmDoc.data() })));
            }
          }
        } else {
          alert("MOC not found");
          navigate('/ops/shipments');
        }
      } catch (error) {
        console.error("Error fetching MOC:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMoc();
  }, [db, mocId, navigate]);

  // Real-time subscription to dispatches for the timeline
  useEffect(() => {
    if (!moc?.orderId) return;
    const q = query(collection(db, "dispatches"), where("orderId", "==", moc.orderId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dispatchesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDispatches(dispatchesList);
    });
    return () => unsubscribe();
  }, [db, moc?.orderId]);

  useEffect(() => {
    if (!moc) return;

    const fetchWarehouseDetails = async () => {
      try {
        if (moc.sourceWarehouseID) {
          const srcDoc = await getDoc(doc(db, 'warehouses', moc.sourceWarehouseID));
          setSourceWarehouse(srcDoc.exists() ? { id: srcDoc.id, ...srcDoc.data() } : null);
        } else {
          setSourceWarehouse(null);
        }

        if (moc.typeOfMOC === 'Interbranch Transfer' && moc.customerName) {
          const destQuery = query(collection(db, 'warehouses'), where('name', '==', moc.customerName));
          const destSnap = await getDocs(destQuery);
          setDestinationWarehouse(destSnap.docs.length > 0 ? { id: destSnap.docs[0].id, ...destSnap.docs[0].data() } : null);
        } else {
          setDestinationWarehouse(null);
        }
      } catch (error) {
        console.error('Error fetching warehouse details:', error);
        setSourceWarehouse(null);
        setDestinationWarehouse(null);
      }
    };

    fetchWarehouseDetails();
  }, [db, moc]);

  const calculateFulfillment = async (orderId, currentMocId = null) => {
      const q = query(collection(db, "dispatches"), where("orderId", "==", orderId));
      const snap = await getDocs(q);
      const map = {};
      snap.docs.forEach(docSnap => {
          if (docSnap.id === currentMocId) return; 
          const data = docSnap.data();
          if (['packing_rejected', 'fin_rejected'].includes(data.dispatchStatus)) return; 
          
          data.components?.forEach(c => {
              if (!map[c.sku]) map[c.sku] = { new: 0, rental: 0 };
              map[c.sku].new += (parseInt(c.newQty) || 0);
              map[c.sku].rental += (parseInt(c.rentalQty) || 0);
          });
      });
      return map;
  };

  const getOpsReviewHistory = (mocData) => {
    const safeMocData = mocData ?? {};

    if (Array.isArray(safeMocData.opsReviewHistory) && safeMocData.opsReviewHistory.length > 0) {
      return safeMocData.opsReviewHistory;
    }

    if (String(safeMocData.opsReviewRemarks || '').trim()) {
      return [{
        remark: safeMocData.opsReviewRemarks,
        reviewedBy: safeMocData.opsReviewedBy || '',
        reviewedAt: safeMocData.opsReviewedAt || '',
        status: safeMocData.opsReviewStatus || safeMocData.dispatchStatus || ''
      }];
    }

    return [];
  };

  const latestOpsReview = getOpsReviewHistory(moc ?? {}).slice(-1)[0] || null;
  const latestOpsReviewStatus = latestOpsReview?.status || '';
  const hasLatestOpsReviewRemark = String(latestOpsReview?.remark || '').trim().length > 0;
  const isOpsReviewReturned = latestOpsReviewStatus === 'rejected' || moc?.dispatchStatus === 'moc_rejected';
  const isOpsReviewApproved = latestOpsReviewStatus === 'approved' || ['moc_pending_finance', 'packing_queued'].includes(moc?.dispatchStatus);
  const showPreviousOpsReviewForManager =
    isOpsManager(user) &&
    moc?.dispatchStatus === 'pending_ops_approval' &&
    isOpsReviewReturned &&
    hasLatestOpsReviewRemark;
  const showOpsReviewBanner =
    showPreviousOpsReviewForManager ||
    (
      isOpsUser(user) &&
      !isOpsManager(user) &&
      hasLatestOpsReviewRemark &&
      (isOpsReviewReturned || isOpsReviewApproved)
    );

  const getStatusBadgeMeta = (status) => {
    const sharedStatusMeta = getDispatchStatusBadgeMeta(status);
    if (sharedStatusMeta) {
      return {
        label: sharedStatusMeta.label,
        backgroundColor: sharedStatusMeta.colors.backgroundColor,
        color: sharedStatusMeta.colors.color,
        borderColor: sharedStatusMeta.colors.borderColor
      };
    }

    const statusMap = {
      moc_rejected: { label: 'Returned for Changes', backgroundColor: '#fff7ed', color: '#9a3412', borderColor: '#fdba74' },
      fin_hold: { label: 'Finance Hold', backgroundColor: '#fffbeb', color: '#92400e', borderColor: '#f59e0b' },
      fin_rejected: { label: 'Rejected by Finance', backgroundColor: '#fef2f2', color: '#991b1b', borderColor: '#fecaca' },
      packing: { label: 'Packing in Progress', backgroundColor: '#eff6ff', color: '#1d4ed8', borderColor: '#93c5fd' },
    };

    if (!status) {
      const defaultDraftMeta = getDispatchStatusBadgeMeta('moc_draft');
      return {
        label: defaultDraftMeta.label,
        backgroundColor: defaultDraftMeta.colors.backgroundColor,
        color: defaultDraftMeta.colors.color,
        borderColor: defaultDraftMeta.colors.borderColor
      };
    }

    return statusMap[status] || {
      label: String(status).replace(/_/g, ' ').toUpperCase(),
      backgroundColor: '#f8fafc',
      color: '#475569',
      borderColor: '#cbd5e1'
    };
  };

  const statusBadgeMeta = getStatusBadgeMeta(moc?.dispatchStatus);
  const sharedHeaderStatus = moc?.dispatchStatus || 'moc_draft';
  const hasSharedHeaderStatusBadge = Boolean(getDispatchStatusBadgeMeta(sharedHeaderStatus));

  const getSalesManagerApprovalTime = (orderData = {}) => {
    if (Array.isArray(orderData.salesReviewHistory)) {
      const approvalReview = [...orderData.salesReviewHistory]
        .reverse()
        .find(review => review?.status === 'in_que' && review?.reviewedAt);

      if (approvalReview?.reviewedAt) return approvalReview.reviewedAt;
    }

    return orderData.salesReviewedAt || '';
  };

  const getSalesManagerApprovalName = (orderData = {}) => {
    if (Array.isArray(orderData.salesReviewHistory)) {
      const approvalReview = [...orderData.salesReviewHistory]
        .reverse()
        .find(review => review?.status === 'in_que' && (review?.reviewedBy || review?.reviewerName));

      if (approvalReview?.reviewedBy || approvalReview?.reviewerName) {
        return approvalReview.reviewedBy || approvalReview.reviewerName || '';
      }
    }

    return orderData.salesReviewedBy || '';
  };

  const salesManagerApprovedAt = getSalesManagerApprovalTime(linkedOrder || order || {});
  const salesManagerApprovedBy = getSalesManagerApprovalName(linkedOrder || order || {});

  const validateApprovalSubmission = () => {
    const nextErrors = {};
    const aluminiumWeightValue = moc?.aluminiumWeight ?? '';

    if (!String(moc?.materialCode || '').trim()) nextErrors.materialCode = true;
    if (!String(moc?.warehouse || '').trim()) nextErrors.warehouse = true;
    if (!String(moc?.customerName || '').trim()) nextErrors.customerName = true;
    if (!String(moc?.preparedBy || '').trim()) nextErrors.preparedBy = true;
    if (!String(aluminiumWeightValue).trim()) nextErrors.aluminiumWeight = true;
    if (!String(moc?.totalMeterNumber || '').trim()) nextErrors.totalMeterNumber = true;

    setValidationErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      alert('Please fill all required fields highlighted in red before submitting for approval.');
      return false;
    }

    return true;
  };

  const handleStatusUpdate = async (newDispatchStatus, newOrderStatus, confirmMsg, successMsg, reviewStatus = '') => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    
    setIsUpdating(true);
    try {
      const reviewerName = user?.displayName || user?.email || '';
      const reviewText = opsReviewRemarks.trim();
      const reviewTimestamp = new Date().toISOString();
      const shouldStoreReview = reviewStatus === 'approved' || reviewStatus === 'rejected' || reviewText.length > 0;
      const reviewEntry = shouldStoreReview ? {
        remark: reviewText,
        reviewedBy: reviewerName,
        reviewedAt: reviewTimestamp,
        status: reviewStatus || newDispatchStatus
      } : null;
      const nextReviewHistory = reviewEntry
        ? [...getOpsReviewHistory(moc ?? {}), reviewEntry]
        : getOpsReviewHistory(moc ?? {});

      // 1. Update Current MOC
      const mocUpdatePayload = {
        dispatchStatus: newDispatchStatus,
        updatedAt: serverTimestamp()
      };

      if (reviewEntry) {
        mocUpdatePayload.opsReviewRemarks = reviewEntry.remark;
        mocUpdatePayload.opsReviewedBy = reviewEntry.reviewedBy;
        mocUpdatePayload.opsReviewedAt = reviewEntry.reviewedAt;
        mocUpdatePayload.opsReviewStatus = reviewEntry.status;
        mocUpdatePayload.opsReviewHistory = nextReviewHistory;
      }

      if (reviewStatus === 'approved') {
        mocUpdatePayload.quantityVerifiedBy = 'Ghanshyam Prajapat';
        mocUpdatePayload.opsApprovedBy = 'Ghanshyam Prajapat';
        mocUpdatePayload.opsApprovedAt = reviewTimestamp;
      }

      await updateDoc(doc(db, "dispatches", mocId), mocUpdatePayload);
      await updateDispatchAudit(db, {
        dispatchId: mocId,
        dispatchData: {
          ...moc,
          dispatchStatus: newDispatchStatus
        },
        user,
        historyEvent: {
          action: newDispatchStatus === 'packing_queued'
            ? 'packing_queued'
            : reviewStatus === 'approved'
              ? 'moc_approved_by_ops_head'
              : reviewStatus === 'rejected'
                ? 'moc_rejected_by_ops_head'
                : 'moc_submitted',
          dispatchStatus: newDispatchStatus,
          timestamp: reviewTimestamp,
          remarks: reviewText
        }
      });

      // 2. If approved, handle splitting logic before moving to finance
      if (newDispatchStatus === 'moc_pending_finance' && moc.orderId) {
          const fulfillmentMap = await calculateFulfillment(moc.orderId, mocId);
          
          const remainingComponents = moc.components.map(c => {
              // Note: We use fulfillmentMap which excludes current MOC
              const prevMocd = (fulfillmentMap[c.sku]?.new || 0) + (fulfillmentMap[c.sku]?.rental || 0);
              const currentMocd = (c.newQty || 0) + (c.rentalQty || 0);
              const totalMocdSoFar = prevMocd + currentMocd;
              const limit = c.maxOrderQty || c.orderQty || 0;
              const balance = Math.max(0, limit - totalMocdSoFar);

              if (balance > 0) {
                  return {
                      ...c,
                      orderQty: balance,
                      newQty: moc.typeOfMOC === 'Rental' ? 0 : balance,
                      rentalQty: moc.typeOfMOC === 'Rental' ? balance : 0,
                      ticked: true
                  };
              }
              return null;
          }).filter(c => c !== null);

          if (remainingComponents.length > 0) {
              const nextMocNo = await getNextMocNumber(db);
              const draftPayload = {
                  ...moc,
                  mocNo: nextMocNo,
                  mocDate: new Date().toISOString().split('T')[0],
                  components: remainingComponents,
                  dispatchStatus: 'moc_draft',
                  updatedAt: serverTimestamp(),
                  createdAt: serverTimestamp(),
              };
              delete draftPayload.id;
              await addDoc(collection(db, "dispatches"), draftPayload);
              console.log("Auto-created draft MOC for balance:", nextMocNo);
          }
      }

      // 3. Update Order Status
      if (moc.orderId && newDispatchStatus !== 'moc_rejected') {
        await updateDoc(doc(db, "orders", moc.orderId), {
          status: newOrderStatus,
          updatedAt: serverTimestamp()
        });
      }

      setMoc(prev => ({
        ...prev,
        dispatchStatus: newDispatchStatus,
        ...(reviewEntry ? {
          opsReviewRemarks: reviewEntry.remark,
          opsReviewedBy: reviewEntry.reviewedBy,
          opsReviewedAt: reviewEntry.reviewedAt,
          opsReviewStatus: reviewEntry.status,
          opsReviewHistory: nextReviewHistory
        } : {}),
        ...(reviewStatus === 'approved' ? {
          quantityVerifiedBy: 'Ghanshyam Prajapat',
          opsApprovedBy: 'Ghanshyam Prajapat',
          opsApprovedAt: reviewTimestamp
        } : {})
      }));
      setOpsReviewRemarks('');
      alert(successMsg + (newDispatchStatus === 'moc_pending_finance' ? ". Draft for remaining balance created (if any)." : ""));
      
      if (newDispatchStatus === 'packing_queued') {
        navigate('/ops/packing');
      }
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>MOC ${moc.mocNo}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 13px; }
            th { font-weight: bold; background-color: #f2f2f2; }
            .total-meter-number { font-size: 30px; font-weight: 900; color: #000; }
            @page { margin: 1cm; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  if (loading) return <Spinner />;

  const isInterbranchTransfer = moc?.typeOfMOC === 'Interbranch Transfer';

  // --- BUTTON LOGIC ---
  const renderOpsReviewSection = () => {
    if (!isOpsManager(user) || moc.dispatchStatus !== 'pending_ops_approval' || activeTab !== 'moc') return null;

    return (
      <div style={{ marginBottom: '18px' }}>
        <div style={opsReviewCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
            <div>
              <h4 style={{ margin: '0 0 6px 0', color: '#0f172a', fontSize: '16px' }}>Ops Manager Review</h4>
              <p style={{ margin: 0, color: '#64748b', fontSize: '13px', lineHeight: '1.5' }}>
                {isInterbranchTransfer
                  ? 'Approve to move this MOC directly to Packing, or reject it with remarks so Ops can revise and resubmit.'
                  : 'Approve to move this MOC to Finance, or reject it with remarks so Ops can revise and resubmit.'}
              </p>
            </div>
            {latestOpsReview?.reviewedAt && (
              <div style={{ color: '#64748b', fontSize: '12px', whiteSpace: 'nowrap' }}>
                Last review: {new Date(latestOpsReview.reviewedAt).toLocaleString()}
              </div>
            )}
          </div>
          <textarea
            value={opsReviewRemarks}
            onChange={(e) => setOpsReviewRemarks(e.target.value)}
            placeholder="Add remarks for the Ops executive..."
            style={opsReviewTextareaStyle}
            rows={4}
          />
        </div>
      </div>
    );
  };

  const renderOpsDecisionButtons = () => {
    if (!isOpsManager(user) || moc.dispatchStatus !== 'pending_ops_approval') return null;

    const canRejectOpsReview = !isUpdating && opsReviewRemarks.trim().length > 0;

    return (
      <>
        <button
          onClick={() => handleStatusUpdate(
            isInterbranchTransfer ? 'packing_queued' : 'moc_pending_finance',
            isInterbranchTransfer ? 'packing_queued' : 'pending_finance',
            isInterbranchTransfer ? 'Approve this MOC and move it directly to Packing?' : 'Approve this MOC and move it to Finance?',
            isInterbranchTransfer ? 'MOC approved and moved to Packing' : 'MOC approved and moved to Finance',
            'approved'
          )}
          disabled={isUpdating}
          style={approveBtnStyle}
        >
          <CheckCircle size={18} /> Approve
        </button>
        <button
          onClick={() => {
            if (!opsReviewRemarks.trim()) {
              alert('Please add remarks before rejecting this MOC.');
              return;
            }
            handleStatusUpdate(
              'moc_rejected',
              null,
              'Reject this MOC and return it to Ops?',
              'MOC returned to Ops for changes',
              'rejected'
            );
          }}
          disabled={!canRejectOpsReview}
          style={{
            ...rejectBtnStyle,
            backgroundColor: canRejectOpsReview ? '#dc2626' : '#e5e7eb',
            color: canRejectOpsReview ? 'white' : '#94a3b8',
            cursor: canRejectOpsReview ? 'pointer' : 'not-allowed'
          }}
        >
          <AlertCircle size={18} /> Reject MOC
        </button>
      </>
    );
  };

  const renderPrimaryButton = () => {
    const status = moc.dispatchStatus;

    if (isAdminUser(user)) {
      return null;
    }

    if (isOpsManager(user) && status === 'pending_ops_approval') {
      return null;
    }

    if (!status || status === 'moc_draft') {
      return (
        <button 
          onClick={() => {
            if (!validateApprovalSubmission()) return;
            handleStatusUpdate(
              'pending_ops_approval',
              'pending_ops_approval',
              "Submit this MOC for approval?",
              "MOC Submitted for Approval"
            );
          }}
          disabled={isUpdating} 
          style={confirmBtnStyle}
        >
          <Send size={18} /> Submit for Approval
        </button>
      );
    }

    if (status === 'moc_approved' || status === 'confirmed') {
      return (
        <button 
          onClick={() => handleStatusUpdate('packing_queued', 'packing_queued', "Move this MOC to Packing Module?", "Moved to Packing Module")} 
          disabled={isUpdating} 
          style={confirmBtnStyle}
        >
          <Package size={18} /> Queue for Packing
        </button>
      );
    }

    if (status === 'packing_scheduled') {
      return (
        <button 
          onClick={() => handleStatusUpdate('packing', 'packing', "Confirm and start packing?", "Packing Confirmed")} 
          disabled={isUpdating} 
          style={confirmBtnStyle}
        >
          <CheckCircle size={18} /> Confirm Packing
        </button>
      );
    }

    return null;
  };

  const isMocTab = activeTab === 'moc';

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
      {showOpsReviewBanner && (
        <div style={{ backgroundColor: isOpsReviewReturned ? '#fff7ed' : '#eff6ff', border: `1px solid ${isOpsReviewReturned ? '#fdba74' : '#93c5fd'}`, borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <AlertCircle size={24} color={isOpsReviewReturned ? '#ea580c' : '#2563eb'} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: isOpsReviewReturned ? '#9a3412' : '#1d4ed8', fontSize: '16px' }}>
              {showPreviousOpsReviewForManager ? 'Previous Ops Manager Remarks' : isOpsReviewReturned ? 'MOC Returned by Ops Manager' : 'Ops Manager Note'}
            </h3>
            {(latestOpsReview?.reviewedAt || latestOpsReview?.reviewedBy) && (
              <div style={{ marginBottom: '6px', color: isOpsReviewReturned ? '#9a3412' : '#1d4ed8', fontSize: '12px', fontWeight: '700' }}>
                {latestOpsReview?.reviewedAt ? new Date(latestOpsReview.reviewedAt).toLocaleString() : ''}
                {latestOpsReview?.reviewedBy ? ` • ${latestOpsReview.reviewedBy}` : ''}
              </div>
            )}
            <p style={{ margin: 0, color: isOpsReviewReturned ? '#7c2d12' : '#1e3a8a', fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
              <strong>Remarks:</strong> {latestOpsReview?.remark}
            </p>
            {showPreviousOpsReviewForManager ? (
              <p style={{ margin: '10px 0 0 0', color: '#9a3412', fontSize: '13px' }}>
                These are the last remarks from the previous review. You can use them while reviewing this resubmission.
              </p>
            ) : isOpsReviewReturned ? (
              <p style={{ margin: '10px 0 0 0', color: '#9a3412', fontSize: '13px' }}>
                Please revise the MOC and submit it again for approval.
              </p>
            ) : (
              <p style={{ margin: '10px 0 0 0', color: '#1d4ed8', fontSize: '13px' }}>
                This is an informational note from the Ops Manager. No action is required from your side.
              </p>
            )}
          </div>
        </div>
      )}

      {/* FIN HOLD BANNER */}
      {moc.dispatchStatus === 'fin_hold' && (
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <Clock size={24} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#b45309', fontSize: '16px' }}>MOC On Hold (Finance)</h3>
            <p style={{ margin: 0, color: '#92400e', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Reason:</strong> {moc.finHoldReason || 'Awaiting finance clarification.'}
            </p>
            <p style={{ margin: '10px 0 0 0', color: '#78350f', fontSize: '13px' }}>
              This MOC is paused. You cannot proceed until finance resolves the hold.
            </p>
          </div>
        </div>
      )}

      {/* FIN REJECTED BANNER */}
      {moc.dispatchStatus === 'fin_rejected' && (
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #ef4444', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <AlertCircle size={24} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#991b1b', fontSize: '16px' }}>MOC Rejected by Finance</h3>
            <p style={{ margin: 0, color: '#b91c1c', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Reason:</strong> {moc.finRejectionReason || 'No specific reason provided.'}
            </p>
            <p style={{ margin: '10px 0 0 0', color: '#7f1d1d', fontSize: '13px' }}>
              This MOC has been rejected and returned to the Sales team. It can no longer be edited or submitted by Operations.
            </p>
          </div>
        </div>
      )}

      {/* Header Buttons */}
      <div className="no-print" style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/ops/shipments')} style={backBtnStyle}>
            <ArrowLeft size={20} />
          </button>
          <h2 style={{ margin: 0 }}>MOC Preview: {moc.mocNo}</h2>
          {hasSharedHeaderStatusBadge ? (
            <StatusBadge status={sharedHeaderStatus} />
          ) : (
            <span style={{
              ...statusBadgeStyle,
              backgroundColor: statusBadgeMeta.backgroundColor,
              color: statusBadgeMeta.color,
              borderColor: statusBadgeMeta.borderColor
            }}>
              {statusBadgeMeta.label}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {!isOpsManager(user) && (!moc.dispatchStatus || ['moc_draft', 'moc_rejected'].includes(moc.dispatchStatus)) && moc.dispatchStatus !== 'fin_hold' && (
            <button onClick={() => navigate(`/ops/moc/edit/${moc.id}`)} style={editBtnStyle}>
              <Edit2 size={18} /> Edit
            </button>
          )}

          {moc.dispatchStatus !== 'fin_hold' && renderPrimaryButton()}
          {renderOpsDecisionButtons()}

          <button onClick={handlePrint} style={secondaryBtnStyle}>
            <Printer size={18} /> Export PDF
          </button>
        </div>
      </div>

      <div style={tabCardStyle}>
        <div style={tabContainerStyle}>
          <button
            style={activeTab === 'moc' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('moc')}
          >
            MOC Preview
          </button>
          <button
            style={activeTab === 'order' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('order')}
          >
            Order Preview
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          {isMocTab ? (
            <>
              {renderOpsReviewSection()}
              <MOCPreviewContent
                mocData={{
                  ...moc,
                  salesManagerApprovedAt,
                  salesManagerApprovedBy
                }}
                sourceWarehouse={sourceWarehouse}
                destinationWarehouse={destinationWarehouse}
                validationErrors={validationErrors}
                printRef={printRef}
              />
            </>
          ) : (
            linkedOrder && customer && firms.length > 0 ? (
              <div style={{ padding: '8px 4px 4px 4px', overflowX: 'auto' }}>
                <OrderPreview order={linkedOrder} customer={customer} firms={firms} db={db} user={user} />
              </div>
            ) : (
              <Spinner />
            )
          )}
        </div>
      </div>

      {/* FOOTER TIMELINE */}
      {!loading && moc && (
        <div style={{ 
          marginTop: '40px', 
          maxWidth: '1000px', 
          margin: '50px auto 20px auto',
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            color: '#374151', 
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Clock size={18} color="#2563eb" /> Order Timeline
          </h3>
          <HorizontalTimeline db={db} orderData={order} dispatchesData={dispatches} dispatchData={moc} />
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `@media print { .no-print { display: none !important; } }`}} />
    </div>
  );
};

// --- STYLES ---
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '10px 0', borderBottom: '1px solid #e2e8f0' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const statusBadgeStyle = { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '999px', border: '1px solid', fontSize: '12px', fontWeight: '700', lineHeight: 1, whiteSpace: 'nowrap' };
const editBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const confirmBtnStyle = { backgroundColor: '#059669', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const secondaryBtnStyle = { backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const opsReviewCardStyle = { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)' };
const opsReviewTextareaStyle = { width: '100%', boxSizing: 'border-box', display: 'block', minHeight: '100px', resize: 'vertical', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px', fontSize: '14px', outline: 'none', marginBottom: '12px' };
const approveBtnStyle = { ...confirmBtnStyle, backgroundColor: '#059669' };
const rejectBtnStyle = { backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const tabCardStyle = { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '20px' };
const tabContainerStyle = { display: 'flex', gap: '10px', padding: '15px 20px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f8fafc' };
const tabStyle = { padding: '10px 16px', borderRadius: '8px', border: '1px solid transparent', backgroundColor: 'transparent', color: '#64748b', fontWeight: '600', cursor: 'pointer' };
const activeTabStyle = { ...tabStyle, backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' };
export default MOCPreview;
